import PropTypes from "prop-types";
import { useCallback, useMemo } from "react";

import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { selectColumn } from "components/BaseTableV3/selectColumn";
import { getNextQueryParams } from "components/helpers";
import RuvixxDate from "components/RuvixxDate";
import usePageTitle from "hooks/usePageTitle";
import TableIconBadge from "routes/custom_tables/TableIconBadge";
import { EVENT_FILTER, omitScopedFilters } from "routes/swoogo/filterOptions";
import { linkCell } from "routes/swoogo/linkCell";
import { swoogoSessionKeys } from "routes/swoogo/queryKeys";
import useSwoogoConnected from "routes/swoogo/useSwoogoConnected";
import SwoogoService from "services/Swoogo";

const HEADER = "Swoogo Sessions";

const sessionCell = linkCell(({ id }) => `/swoogo/sessions/${id}`);

const COLUMNS = [
  selectColumn,
  { id: "ID", accessorKey: "id", header: "ID", size: 90, cell: sessionCell },
  {
    id: "Name",
    accessorKey: "name",
    header: "Name",
    size: 260,
    cell: sessionCell,
  },
  {
    id: "Event",
    accessorKey: "event_name",
    sortKey: "event_id",
    header: "Event",
    size: 240,
    cell: linkCell(({ event_id }) => `/swoogo/events/${event_id}`),
  },
  { id: "Date", accessorKey: "date", header: "Date", size: 120 },
  { id: "Start Time", accessorKey: "start_time", header: "Start Time" },
  {
    id: "End Time",
    accessorKey: "end_time",
    header: "End Time",
    enableSorting: false,
  },
  {
    id: "Time Zone",
    accessorKey: "timezone",
    header: "Time Zone",
    enableSorting: false,
  },
  {
    id: "Last Synced",
    accessorKey: "last_synced_at",
    header: "Last Synced",
    size: 160,
    cell: ({ row }) => {
      const date = row.getValue("Last Synced");
      return date ? <RuvixxDate date={date} /> : null;
    },
  },
];

const FILTERS = [EVENT_FILTER];

const SessionsTable = ({ scope }) => {
  usePageTitle(scope ? null : HEADER);

  const { lastSyncedAt } = useSwoogoConnected();
  const queryKey = useMemo(
    () => swoogoSessionKeys.list(lastSyncedAt),
    [lastSyncedAt]
  );
  const filters = useMemo(() => omitScopedFilters(FILTERS, scope), [scope]);

  const fetchSessions = useCallback(
    async queryParams =>
      SwoogoService.listSessions(getNextQueryParams(queryParams, COLUMNS)),
    []
  );

  return (
    <BaseTableV3
      columns={COLUMNS}
      headerIcon={<TableIconBadge iconName="presentation" />}
      header={HEADER}
      tableName="swoogo_sessions"
      queryKey={queryKey}
      fetchData={fetchSessions}
      filters={filters}
      extraQueryParams={scope}
      enableSearch
      enableSettings
    />
  );
};

SessionsTable.propTypes = {
  scope: PropTypes.objectOf(PropTypes.number),
};

export default SessionsTable;
